import {
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { KeyboardArrowLeft } from "@mui/icons-material";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import UserAvatar from "../common/UserAvatar";
import { selectCurrentUser } from "../pages/auth/authSlice";
import { useGetProfileQuery } from "../pages/dashboard/dashboardApiSlice";
import { useGetTeacherSubjectsQuery } from "../../app/questionApiSlice";
import { useGetMyStudentProfileQuery } from "../../app/parentApiSlice";
import { useGetStudentClassesQuery } from "../../app/quizApiSlice";
import overviewIcon from "../../assets/icons/student-icon.svg";
import quizzesIcon from "../../assets/icons/quiz-icon-overview.svg";
import questionsIcon from "../../assets/icons/bulk-question-icon.svg";
import reviewIcon from "../../assets/icons/review-icon.svg";
import notesIcon from "../../assets/icons/notes-icon.svg";

function LmsSideNav({ drawerWidth, handleDrawerToggle }) {
  const theme = useTheme();
  const contrastText = theme.palette.primary.contrastText;

  const user = useSelector(selectCurrentUser);

  // Derive roles synchronously before any conditional queries
  const userRoles = user?.roles?.map((item) => item?.role?.name).filter(Boolean) || [];
  const isTeacher = userRoles.includes("teacher");
  const isReviewer = userRoles.includes("reviewer");
  const isStudent = userRoles.includes("student");
  const isAdmin = userRoles.includes("admin");
  const isExaminer = userRoles.includes("examiner");
  // User can create/manage quizzes if they are an examiner or a teacher
  const canManageQuizzes = isExaminer || isTeacher;
  // Admin-only (no examiner/teacher role): view-only in LMS
  const isAdminOnly = isAdmin && !canManageQuizzes;

  const { data: teacherProfile } = useGetProfileQuery(undefined, { skip: !isTeacher });
  const { data: teacherSubjects } = useGetTeacherSubjectsQuery(undefined, { skip: !isTeacher });
  const { data: studentProfile } = useGetMyStudentProfileQuery(undefined, { skip: !isStudent });
  const { data: studentClasses } = useGetStudentClassesQuery(
    { studentId: studentProfile?.id },
    { skip: !isStudent || !studentProfile?.id }
  );
  const studentClass = studentClasses?.[0] ?? null;

  const hasSubjects = teacherSubjects && teacherSubjects.length > 0;

  const STAFF_ROLES = ["admin", "examiner", "accountant", "webmaster", "agent"];
  const isAdminLike = userRoles.some((r) => STAFF_ROLES.includes(r));

  // Reviewer-only: has reviewer role, is not a teacher (or is a teacher with no subjects), and has no other staff roles
  const isReviewerOnly = isReviewer && !isAdminLike && (!isTeacher || !hasSubjects);

  // Build navigation items based on roles
  const navItems = isStudent
    ? [
        { text: "Overview", icon: overviewIcon, link: "/lms/student/overview" },
        { text: "Notes", icon: notesIcon, link: "/lms/student/notes" },
        ...(studentClass
          ? [{ text: "Class Quiz", icon: quizzesIcon, link: "/lms/student/class-quiz" }]
          : []),
        { text: "Game", muiIcon: <SportsEsportsIcon sx={{ fontSize: "20px", color: "inherit" }} />, link: "/lms/student/quiz" },
      ]
    : isAdminOnly
    ? [
        { text: "Questions", icon: questionsIcon, link: "/lms/questions" },
        { text: "Quizzes", icon: quizzesIcon, link: "/lms/quizzes" },
      ]
    : canManageQuizzes && !isTeacher
    ? [
        { text: "Questions", icon: questionsIcon, link: "/lms/questions" },
        { text: "Quizzes", icon: quizzesIcon, link: "/lms/quizzes" },
      ]
    : isReviewerOnly
    ? [
        { text: "Overview", icon: overviewIcon, link: "/lms/overview" },
        { text: "Reviews", icon: reviewIcon, link: "/lms/reviews" },
      ]
    : [
        { text: "Overview", icon: overviewIcon, link: "/lms/overview" },
        { text: "Questions", icon: questionsIcon, link: "/lms/questions" },
        { text: "Notes", icon: notesIcon, link: "/lms/notes" },
        { text: "Quizzes", icon: quizzesIcon, link: "/lms/quizzes" },
        ...(isReviewer
          ? [{ text: "Reviews", icon: reviewIcon, link: "/lms/reviews" }]
          : []),
        { text: "Leaderboard", muiIcon: <EmojiEventsIcon sx={{ fontSize: "20px", color: "inherit" }} />, link: "/lms/leaderboard" },
      ];

  const roleLabel = isStudent ? "Student" : isAdminOnly ? "Admin" : isExaminer && !isTeacher ? "Examiner" : isReviewerOnly ? "Reviewer" : "Teacher";
  const studentName = `${studentProfile?.first_name || ""} ${studentProfile?.last_name || ""}`.trim();
  const teacherName = `${teacherProfile?.first_name || user?.profile?.first_name || user?.first_name || ""} ${teacherProfile?.last_name || user?.profile?.last_name || user?.last_name || ""}`.trim();
  const displayName = isStudent
    ? studentName || user?.username || user?.schoola_id || "Student"
    : teacherName || user?.username || user?.schoola_id || roleLabel;

  return (
    <Box
      sx={{
        mx: 3,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* User Profile Section */}
      <Box
        sx={{
          margin: "2em 0 3em 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <UserAvatar
              src={isStudent ? studentProfile?.photo : teacherProfile?.photo}
              name={displayName}
              sx={{
                width: 80,
                height: 80,
                border: `3px solid ${alpha(contrastText, 0.8)}`,
                bgcolor: alpha(contrastText, 0.2),
                color: contrastText,
              }}
            />
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "rgba(0,0,0,0.6)",
                  textTransform: "capitalize",
                  backgroundColor: "rgba(255,255,255,0.85)",
                  borderRadius: "4px",
                  px: 1,
                  py: 0.25,
                  lineHeight: 1.8,
                }}
              >
                {roleLabel}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.75,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: contrastText,
                  textTransform: "capitalize",
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{ display: { xs: "block", md: "none" }, color: contrastText, position: "absolute", right: 0 }}
          >
            <KeyboardArrowLeft sx={{ color: "#95969D" }} />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ backgroundColor: alpha(contrastText, 0.2), mb: 2 }} />

      {/* Navigation Items */}
      <List
        sx={{
          flex: 1,
          "& .MuiTypography-root": {
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "25px",
            color: "inherit",
          },
          "& a": {
            color: contrastText,
            textDecoration: "none",
            borderRadius: "8px",
            width: "100%",
          },
        }}
      >
        {navItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
            <Box
              component={NavLink}
              to={item.link}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: "12px",
                borderRadius: "8px",
                textDecoration: "none",
                color: "inherit",
                "&.active": {
                  backgroundColor: alpha(contrastText, 0.23),
                },
                "&:hover": {
                  backgroundColor: alpha(contrastText, 0.15),
                },
                transition: "background-color 0.3s ease",
              }}
              onClick={handleDrawerToggle}
            >
              <Box
                sx={{
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.muiIcon ? (
                  item.muiIcon
                ) : (
                  <img src={item.icon} alt={item.text} style={{ width: "100%", filter: "brightness(0) invert(1)" }} />
                )}
              </Box>
              <ListItemText primary={item.text} />
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

export default LmsSideNav;
